import { hasTeamRole, type Studio } from '@greenroom/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Ellipsis, RefreshCw, Settings, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { RecordingList } from '../components/recording-list'
import { StudioSettingsDialog, useStudioCache } from '../components/studio-dialogs'
import { Button, LinkButton } from '../components/ui/button'
import { ConfirmDialog } from '../components/ui/dialog'
import { Menu, MenuItem } from '../components/ui/menu'
import { Badge, CopyButton, PageHeader, Section } from '../components/ui/misc'
import { FullPageSpinner } from '../components/ui/spinner'
import { api } from '../lib/api'
import { useCurrentTeam, useUser } from '../lib/auth'
import { formatDate } from '../lib/format'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'
import { QueryError } from './errors'

export function StudioPage() {
  const { studioId = '' } = useParams()
  const studio = useQuery(q.studio(studioId))
  useTitle(studio.data?.name, 'Studios')
  if (studio.isPending) return <FullPageSpinner />
  if (studio.isError) return <QueryError error={studio.error} retry={studio.refetch} what="studio" />
  return <StudioDetail studio={studio.data} />
}

function StudioDetail({ studio }: { studio: Studio }) {
  const me = useUser()
  const role = me.teams.find((t) => t.id === studio.teamId)?.role ?? 'viewer'
  const canProduce = hasTeamRole(role, 'producer')
  const isOwner = role === 'owner'
  const { setTeam } = useCurrentTeam()
  const recordings = useQuery(q.recordings(studio.teamId, studio.id))
  const cache = useStudioCache()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<'settings' | 'regenerate' | 'delete' | null>(null)

  const regenerate = useMutation({
    mutationFn: () => api<Studio>(`/api/studios/${studio.id}/guest-link`, { method: 'POST' }),
    onSuccess: cache.put,
    meta: { silent: true },
  })
  const remove = useMutation({
    mutationFn: () => api(`/api/studios/${studio.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      cache.remove(studio)
      setTeam(studio.teamId)
      navigate('/studios', { replace: true })
    },
    meta: { silent: true },
  })

  return (
    <>
      <PageHeader
        back={
          <LinkButton to="/studios" variant="ghost" size="sm" icon={<ArrowLeft className="size-4" />} className="-ml-3">
            Studios
          </LinkButton>
        }
        title={studio.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            Created {formatDate(studio.createdAt)}
            {studio.live.participants > 0 && <Badge tone="ok">{studio.live.participants} in studio now</Badge>}
            {studio.live.recording && <Badge tone="rec">Recording</Badge>}
          </span>
        }
        actions={
          <>
            <LinkButton to={`/studios/${studio.id}/live`} variant="primary">
              Enter studio
            </LinkButton>
            {canProduce && <LinkButton to={`/recordings/import?studioId=${studio.id}`}>Import recording</LinkButton>}
            {canProduce && (
              <Button icon={<Settings className="size-4" />} onClick={() => setDialog('settings')}>
                Settings
              </Button>
            )}
            {isOwner && (
              <Menu
                trigger={
                  <Button size="icon" aria-label="More">
                    <Ellipsis className="size-4" />
                  </Button>
                }
              >
                <MenuItem icon={<Trash2 />} danger onSelect={() => setDialog('delete')}>
                  Delete studio
                </MenuItem>
              </Menu>
            )}
          </>
        }
      />

      <div className="mb-8 grid gap-4 lg:grid-cols-[2fr_1fr]">
        {canProduce && studio.guestUrl ? (
          <Section
            title="Guest link"
            actions={
              <Button size="sm" variant="ghost" icon={<RefreshCw className="size-3.5" />} onClick={() => setDialog('regenerate')}>
                New link
              </Button>
            }
          >
            <p className="mb-3 text-sm text-muted">Guests join with this link from their browser. They need no account and nothing to install.</p>
            <div className="flex gap-2">
              <input readOnly value={studio.guestUrl} className="input font-mono text-[13px]" onFocus={(e) => e.currentTarget.select()} aria-label="Guest link" />
              <CopyButton text={studio.guestUrl} what="Guest link" />
            </div>
          </Section>
        ) : (
          <Section title="Joining">
            <p className="text-sm text-muted">Team members enter with the button above. Producers can share a guest link for people outside the team.</p>
          </Section>
        )}
        <Section title="Recording">
          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Local quality</dt>
              <dd>{studio.videoQuality}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Cloud backup</dt>
              <dd>{studio.backupRecording ? 'On' : 'Off'}</dd>
            </div>
          </dl>
        </Section>
      </div>

      <h2 className="mb-4 text-lg font-semibold">Recordings</h2>
      {recordings.isPending ? (
        <FullPageSpinner />
      ) : recordings.isError ? (
        <QueryError error={recordings.error} retry={recordings.refetch} what="list" />
      ) : (
        <RecordingList recordings={recordings.data} />
      )}

      <StudioSettingsDialog studio={studio} open={dialog === 'settings'} onOpenChange={(o) => setDialog(o ? 'settings' : null)} />
      <ConfirmDialog
        open={dialog === 'regenerate'}
        onOpenChange={(o) => setDialog(o ? 'regenerate' : null)}
        title="Create a new guest link?"
        description="The current link stops working right away. People already in the studio stay connected, but anyone who reloads needs the new link."
        confirmLabel="Create new link"
        onConfirm={() => regenerate.mutateAsync()}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(o) => setDialog(o ? 'delete' : null)}
        title={`Delete ${studio.name}?`}
        description="The studio and its guest link go away. Its recordings stay in the team's recording list."
        confirmLabel="Delete studio"
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </>
  )
}
